import { apiFetch } from '../utilities/apiFetch';
import type {
  ICreditRequestDetailResponse,
  ICreditRequestSaveRequest,
  ICreditRequestTransitionResponse,
} from '../models/credit-request';
import { CreditRequestTransition } from '../models/credit-request';

export interface ICreditRequestCreateRequest {
  orderId: number;
  form: ICreditRequestSaveRequest;
}

const transitionPaths: Record<CreditRequestTransition, string> = {
  [CreditRequestTransition.Submit]: 'submit',
  [CreditRequestTransition.Resubmit]: 'resubmit',
  [CreditRequestTransition.Return]: 'return',
  [CreditRequestTransition.SendToAccountsReceivable]: 'send-to-accounts-receivable',
  [CreditRequestTransition.Reject]: 'reject',
  [CreditRequestTransition.Close]: 'close',
  [CreditRequestTransition.Complete]: 'complete',
};

const requiredResponse = (response: Response | undefined): Response => {
  if (!response) throw new Error('Credit request API request failed.');
  return response;
};

export class CreditRequestApi {
  public async get(id: number): Promise<ICreditRequestDetailResponse> {
    const response = requiredResponse(await apiFetch(`/api/credit-requests/${id}`));
    return response.json();
  }

  public async create(request: ICreditRequestCreateRequest): Promise<ICreditRequestDetailResponse> {
    const response = requiredResponse(await apiFetch(
      `/api/credit-requests?orderId=${request.orderId}`,
      { method: 'post', body: JSON.stringify(request.form) },
    ));
    return response.json();
  }

  public async save(id: number, request: ICreditRequestSaveRequest): Promise<ICreditRequestDetailResponse> {
    const response = requiredResponse(await apiFetch(`/api/credit-requests/${id}`, {
      method: 'put',
      body: JSON.stringify(request),
    }));
    return response.json();
  }

  public async transition(
    id: number,
    transition: CreditRequestTransition,
    rowVersion: string,
    comment: string,
  ): Promise<ICreditRequestTransitionResponse> {
    const response = requiredResponse(await apiFetch(
      `/api/credit-requests/${id}/${transitionPaths[transition]}`,
      {
        method: 'post',
        body: JSON.stringify({ rowVersion, comment }),
      },
    ));
    return response.json();
  }

  public async downloadPdf(id: number): Promise<Blob | null> {
    const response = await apiFetch(`/api/credit-requests/${id}/pdf`);
    if (!response) return null;
    return response.blob();
  }
}
